import type {
  POIUpdate,
  POIBackendFeature,
  InternalPOIDetails,
  FiltersUpdate,
  TourFilter,
} from '../../apis/api-tours.ts';
import {toEPSG4326} from '../../map/proj.ts';

function asPOIBackendFeature(p: InternalPOIDetails): POIBackendFeature {
  return {
    type: 'Feature',
    id: p.id,
    properties: {
      title: p.title,
      description: p.description,
      photo: p.photo,
    },
    geometry: {
      type: 'Point',
      coordinates: toEPSG4326(p.geometry),
    },
  };
}

export type InternalPOIUpdate = {
  toCreate: InternalPOIDetails[];
  toUpdate: InternalPOIDetails[];
  toDelete: InternalPOIDetails[];
};

export function createInternalPoisUpdate(
  currentPois: InternalPOIDetails[],
  previousPois: InternalPOIDetails[],
): InternalPOIUpdate {
  const toCreate: InternalPOIDetails[] = [];
  const toUpdate: InternalPOIDetails[] = [];

  currentPois.forEach((curPoi) => {
    if (curPoi.id) {
      toUpdate.push(curPoi);
    } else {
      toCreate.push(curPoi);
    }
  });

  const currentIds = currentPois.map((p) => p.id);
  const toDelete = previousPois.filter((p) => !currentIds.includes(p.id));
  return {toUpdate, toCreate, toDelete};
}

export function mapToPoisUpdate(ipu: InternalPOIUpdate): POIUpdate {
  const toCreate: POIBackendFeature[] = ipu.toCreate.map((ip) =>
    asPOIBackendFeature(ip),
  );
  const toUpdate: POIBackendFeature[] = ipu.toUpdate.map((ip) =>
    asPOIBackendFeature(ip),
  );
  const toDeleteIds = ipu.toDelete.map((ip) => ip.id);
  const poisUpdate: POIUpdate = {
    to_update: {
      type: 'FeatureCollection',
      features: toUpdate,
    },
    to_create: {
      type: 'FeatureCollection',
      features: toCreate,
    },
    to_delete: toDeleteIds,
  };
  return poisUpdate;
}

export function createFiltersUpdate(
  editedFilters: TourFilter[],
  backendFilters: TourFilter[],
): FiltersUpdate {
  const toCreate: TourFilter[] = [];
  const toUpdate: TourFilter[] = [];
  let toDelete: number[] = [];

  editedFilters.forEach((filter: TourFilter) => {
    if (filter.id < 0) {
      toCreate.push(filter);
    } else {
      toUpdate.push(filter);
    }
  });

  const currentIds = editedFilters.map((f: TourFilter) => f.id);
  const toDeleteFilters = backendFilters.filter(
    (f: TourFilter) => !currentIds.includes(f.id),
  );
  toDelete = toDeleteFilters.map((f: TourFilter) => f.id);

  return {to_create: toCreate, to_update: toUpdate, to_delete: toDelete};
}

export interface SOUToursAndPoisResult {
  tourId: number;
  pois: {
    created: number[];
    updated: number[];
    deleted: number[];
  };
}
